import {
  FormControlLabel,
  makeStyles,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LeaderboardTrophyIcon,
  Typography
} from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import React, { useState } from 'react'

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    [theme.breakpoints.down('xs')]: {
      margin: '18px'
    }
  },
  dialogTitle: {
    backgroundColor: theme.paintscout.palette.primary,
    fontSize: '2rem',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5)
    }
  },
  dialogContent: {
    padding: theme.spacing(3, 4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2)
    }
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.1rem'
  },
  leaderboardsIcon: {
    width: 30,
    height: 30,
    marginRight: theme.spacing(2)
  },
  confirmButton: {
    background: theme.paintscout.palette.primary,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.paintscout.palette.primary
    }
  },
  contentTitle: {
    fontSize: '30px',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: '24px'
    }
  },
  contentText: {
    fontSize: '18px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '16px'
    }
  },
  spacer: {
    height: theme.spacing(5)
  },
  dialogActions: {
    padding: theme.spacing(1.5, 4),
    height: 'fit-content',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2)
    }
  },
  backButton: {
    marginLeft: theme.spacing(-2),
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(-1.5)
    }
  }
}))

export interface GamificationExitDialogProps extends DialogProps {
  onConfirm: (blockDialog?: boolean) => void
  onCancel: () => void
  allowDontShowMessage?: boolean
  hasJoinedLeaderboard: boolean
  playerName: string
}

export function GamificationExitDialog(props: GamificationExitDialogProps) {
  const { onConfirm, onCancel, allowDontShowMessage, hasJoinedLeaderboard, playerName } = props
  const classes = useStyles(props)
  const [dontShowMessage, setDontShowMessage] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleConfirm = () => {
    if (typeof BroadcastChannel != 'undefined') {
      const channel = new BroadcastChannel('callout')
      channel.postMessage(hasJoinedLeaderboard ? 'confirmed-dialog' : 'closed-dialog')
    }
    setSubmitting(true)
    onConfirm(dontShowMessage)
  }

  return (
    <Dialog {...props} maxWidth="sm" fullScreen={false} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle classes={{ root: classes.dialogTitle }}>
        <Typography variant="body1" className={classes.title}>
          {/* <img src={LeaderboardsIcon} alt="Leaderboards" className={classes.leaderboardsIcon} /> */}
          <LeaderboardTrophyIcon className={classes.leaderboardsIcon} />
          Leaderboards
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="h4" className={classes.contentTitle}>
          Are you sure you want to exit?
        </Typography>
        <Typography className={classes.contentText} style={{ marginBottom: 16 }}>
          {hasJoinedLeaderboard ? (
            <>
              Your anonymous username is <strong>{playerName}</strong>. If you would like to change it, click back.
            </>
          ) : (
            `Complete the walkthrough to access the leaderboards. Not ready? No worries—you can join later from the Dashboard.`
          )}
        </Typography>
        {allowDontShowMessage && (
          <FormControlLabel
            classes={{ label: classes.contentText }}
            style={{ marginTop: 8 }}
            label={`Don't show me this again`}
            control={<Checkbox checked={dontShowMessage} onChange={() => setDontShowMessage(!dontShowMessage)} />}
          />
        )}
        {hasJoinedLeaderboard && !allowDontShowMessage && <div className={classes.spacer} />}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button disabled={submitting} onClick={onCancel} variant="textNoPadding" className={classes.backButton}>
            Back
          </Button>
        }
        classes={{
          root: classes.dialogActions
        }}
      >
        <Button loading={submitting} className={classes.confirmButton} onClick={handleConfirm}>
          Yes, I&lsquo;m Sure
        </Button>
      </DialogActions>
    </Dialog>
  )
}
